/**
 * 账号管理 —— 管理端账号的增删改、重置密码、启用/停用。
 *
 * 此前账号只能靠环境变量播种：改一个密码要动部署并重启，而且没有操作留痕。
 * 现在这些动作都走接口并写审计（user.create / role_change / password_reset /
 * disable / enable / delete），每条都带修改前/后。
 *
 * 两条护栏在服务端（`server/src/users.ts`）：不能对自己下手、不能删/停用最后一个
 * 启用的管理员。前端也会把这些按钮禁掉，但那只是体验优化 —— 服务端 409 才是边界。
 */
import { useState } from 'react'
import {
  KeyRound,
  Loader2,
  Plus,
  ShieldCheck,
  TriangleAlert,
  UserCheck,
  UserX,
  Trash2,
} from 'lucide-react'
import { toast } from 'sonner'
import { api, useAsync, type Role, type UserRow } from '@/lib/api'
import { useAuth, useCan, roleLabel } from '@/lib/auth'
import { PageHeader } from '@/components/PageHeader'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { formatTime } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback
}

export function UsersPage() {
  const { user: me } = useAuth()
  const canWrite = useCan()('user:write')
  const list = useAsync(() => api.listUsers(), [])
  // 角色已改为可管理，下拉选项必须来自服务端，不能再写死三角色
  const roles = useAsync(() => api.listRoles(), [])

  const roleOptions = roles.data?.rows ?? []
  const displayRole = (id: string): string =>
    roleOptions.find((role) => role.id === id)?.name ?? roleLabel(id)

  const [createOpen, setCreateOpen] = useState(false)
  const [form, setForm] = useState({ username: '', password: '', role: 'operator' as Role })
  const [busy, setBusy] = useState<string | null>(null)
  const [resetFor, setResetFor] = useState<UserRow | null>(null)
  const [pendingDelete, setPendingDelete] = useState<UserRow | null>(null)
  const [resetPassword, setResetPassword] = useState('')

  const rows = list.data?.rows ?? []

  async function createUser(e: React.FormEvent) {
    e.preventDefault()
    setBusy('create')
    try {
      await api.createUser(form)
      toast.success(`已创建账号 ${form.username}（首次登录需改密）`)
      setCreateOpen(false)
      setForm({ username: '', password: '', role: 'operator' })
      list.reload()
    } catch (err) {
      toast.error(errorText(err, '创建失败'))
    } finally {
      setBusy(null)
    }
  }

  async function changeRole(row: UserRow, role: string) {
    setBusy(row.id)
    try {
      await api.updateUser(row.id, { role: role as Role })
      toast.success(`已将 ${row.username} 的角色改为 ${displayRole(role)}（其旧会话已失效）`)
      list.reload()
    } catch (err) {
      toast.error(errorText(err, '修改角色失败'))
    } finally {
      setBusy(null)
    }
  }

  async function toggleActive(row: UserRow) {
    setBusy(row.id)
    try {
      await api.updateUser(row.id, { active: row.is_active === 0 })
      toast.success(row.is_active === 0 ? `已启用 ${row.username}` : `已停用 ${row.username}`)
      list.reload()
    } catch (err) {
      toast.error(errorText(err, '操作失败'))
    } finally {
      setBusy(null)
    }
  }

  async function submitReset(e: React.FormEvent) {
    e.preventDefault()
    if (!resetFor) return
    setBusy(resetFor.id)
    try {
      await api.resetUserPassword(resetFor.id, resetPassword)
      toast.success(`已重置 ${resetFor.username} 的密码，其下次登录须改密`)
      setResetFor(null)
      setResetPassword('')
      list.reload()
    } catch (err) {
      toast.error(errorText(err, '重置失败'))
    } finally {
      setBusy(null)
    }
  }

  async function remove(row: UserRow) {
    setBusy(row.id)
    try {
      await api.deleteUser(row.id)
      toast.success(`已删除 ${row.username}`)
      list.reload()
    } catch (err) {
      toast.error(errorText(err, '删除失败'))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="账号管理"
        actions={
          canWrite ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" />
              新建账号
            </Button>
          ) : null
        }
      />

      {list.error ? (
        <div className="flex items-start gap-2 rounded-md border border-[var(--color-destructive)] p-3 text-xs text-[var(--color-destructive)]">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">加载失败：{list.error}</span>
          <Button variant="ghost" size="sm" onClick={list.reload}>
            重试
          </Button>
        </div>
      ) : null}

      {roles.error ? (
        <div className="flex items-start gap-2 rounded-md border border-[var(--color-warning)] p-3 text-xs text-[var(--color-warning)]">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">角色列表加载失败，暂时无法改角色：{roles.error}</span>
          <Button variant="ghost" size="sm" onClick={roles.reload}>
            重试
          </Button>
        </div>
      ) : null}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>账号</TableHead>
                <TableHead className="w-[140px]">角色</TableHead>
                <TableHead className="w-[110px]">状态</TableHead>
                <TableHead className="w-[170px]">创建时间</TableHead>
                <TableHead className="w-[170px]">最后登录</TableHead>
                <TableHead className="w-[260px]">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.loading && !list.data ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={6}
                    className="py-10 text-center text-sm text-[var(--color-muted-foreground)]"
                  >
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="size-4 animate-spin" />
                      正在加载…
                    </span>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={6}
                    className="py-10 text-center text-sm text-[var(--color-muted-foreground)]"
                  >
                    暂无账号
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const isMe = row.id === me?.id
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="text-sm">
                        <span className="flex items-center gap-1.5">
                          {row.username}
                          {isMe ? <Badge variant="outline">当前账号</Badge> : null}
                        </span>
                      </TableCell>
                      <TableCell>
                        {canWrite && !isMe ? (
                          <Select
                            value={row.role}
                            onValueChange={(v) => void changeRole(row, v)}
                            disabled={busy === row.id || roles.loading}
                          >
                            <SelectTrigger className="h-8 w-[110px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {roleOptions.map((r) => (
                                <SelectItem key={r.id} value={r.id}>
                                  {r.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge variant={row.role === 'admin' ? 'default' : 'outline'}>
                            {displayRole(row.role)}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={row.is_active === 1 ? 'success' : 'destructive'}>
                          {row.is_active === 1 ? '启用' : '已停用'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs tabular-nums text-[var(--color-muted-foreground)]">
                        {formatTime(row.created_at)}
                      </TableCell>
                      <TableCell className="text-xs tabular-nums text-[var(--color-muted-foreground)]">
                        {row.last_login_at ? formatTime(row.last_login_at) : '从未'}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={!canWrite || isMe || busy === row.id}
                            title={isMe ? '重置自己的密码请用「账号与密码」页' : undefined}
                            onClick={() => {
                              setResetFor(row)
                              setResetPassword('')
                            }}
                          >
                            <KeyRound className="size-3.5" />
                            重置密码
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={!canWrite || isMe || busy === row.id}
                            title={isMe ? '不能停用自己的账号' : undefined}
                            onClick={() => void toggleActive(row)}
                          >
                            {row.is_active === 1 ? (
                              <UserX className="size-3.5" />
                            ) : (
                              <UserCheck className="size-3.5" />
                            )}
                            {row.is_active === 1 ? '停用' : '启用'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[var(--color-destructive)] hover:text-[var(--color-destructive)]"
                            disabled={!canWrite || isMe || busy === row.id}
                            title={isMe ? '不能删除自己的账号' : undefined}
                            onClick={() => setPendingDelete(row)}
                          >
                            <Trash2 className="size-3.5" />
                            删除
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <p className="flex items-start gap-1.5 text-xs text-[var(--color-muted-foreground)]">
        <ShieldCheck className="mt-0.5 size-3.5 shrink-0" />
        改角色、重置密码、停用都会让该账号
        <strong className="font-medium text-[var(--color-foreground)]">已登录的会话立即失效</strong>
        ；新建账号与重置密码后， 账号首次登录必须先修改密码。
      </p>

      {/* 新建账号 */}
      <Dialog open={createOpen} onOpenChange={(o) => !busy && setCreateOpen(o)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>新建账号</DialogTitle>
          </DialogHeader>
          <form className="space-y-3" onSubmit={createUser}>
            <div className="space-y-1.5">
              <Label htmlFor="new-username">账号</Label>
              <Input
                id="new-username"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                required
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-password">初始密码（至少 8 位）</Label>
              <Input
                id="new-password"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-role">角色</Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm({ ...form, role: v as Role })}
                disabled={roles.loading}
              >
                <SelectTrigger id="new-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                取消
              </Button>
              <Button type="submit" disabled={busy === 'create'}>
                {busy === 'create' ? <Loader2 className="size-4 animate-spin" /> : null}
                创建
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* 删除账号：与设备页同一套二次确认 */}
      {pendingDelete ? (
        <ConfirmDialog
          title="删除账号"
          description={`确定删除账号「${pendingDelete.username}」？该账号将立即失效，操作不可撤销。`}
          confirmLabel="删除"
          danger
          busy={busy === pendingDelete.id}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => {
            const target = pendingDelete
            setPendingDelete(null)
            void remove(target)
          }}
        />
      ) : null}

      {/* 重置密码 */}
      <Dialog open={resetFor !== null} onOpenChange={(o) => !o && !busy && setResetFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>重置密码 · {resetFor?.username}</DialogTitle>
          </DialogHeader>
          <form className="space-y-3" onSubmit={submitReset}>
            <div className="space-y-1.5">
              <Label htmlFor="reset-password">新密码（至少 8 位）</Label>
              <Input
                id="reset-password"
                type="password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                required
                autoFocus
              />
            </div>
            <p className="text-xs text-[var(--color-muted-foreground)]">
              重置后该账号已登录的会话立即失效，且下次登录必须先改成自己的密码。
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setResetFor(null)}>
                取消
              </Button>
              <Button type="submit" disabled={busy !== null}>
                {busy ? <Loader2 className="size-4 animate-spin" /> : null}
                确认重置
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
